'use client'

import Cookies from 'js-cookie'
import { useEffect, useRef, useState } from 'react'
import { render } from 'storyblok-rich-text-react-renderer'

import { StylePanelProps } from '@/lib/stylePanel'

import useResponsivePadding from '../../../lib/useResponsivePadding'
import { ContactService, IContactForm } from '../../../utilities/salesForceForm'
import { PhnxButton } from '../../01-atoms/Button/Button'
import styles from './Form.module.css'

export type FormProps = {
  title?: string
  description?: React.ReactNode
  disclaimer?: React.ReactNode
  disclaimer_align?: string
  formID?: string
  fields: React.ReactNode[][]
  ctaLabel?: string
  ctaVariant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'quinary'
  ctaWidth?: number
  ctaFullMobile?: boolean
  ctaPosition?: 'left' | 'center' | 'right'
  ctaBold?: boolean
  endpoint: string
  alignment?: 'center' | 'left' | 'right'
  content?: React.ReactNode
  subContent?: React.ReactNode
  uuid?: string
  country?: string
  lead_source?: string
  campaign_id?: string
  lead_type?: string
  submission_url?: string
  isSubscribeForm?: boolean
  variant?: string
  shadow?: boolean
  border?: boolean
  accessCookie?: string
} & StylePanelProps

export const PhnxForm: React.FC<FormProps> = ({
  title,
  description,
  disclaimer,
  disclaimer_align = 'center',
  formID: initialFormID = 'contactForm',
  fields,
  ctaLabel,
  ctaVariant = 'primary',
  ctaWidth,
  ctaFullMobile = false,
  ctaPosition = 'center',
  ctaBold = false,
  padding,
  backgroundColor,
  textColor,
  alignment = 'center',
  endpoint,
  subContent,
  uuid,
  lead_source,
  campaign_id,
  lead_type,
  submission_url,
  isSubscribeForm,
  variant,
  shadow,
  border,
  accessCookie,
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [utmSource, setUtmSource] = useState('')
  const [utmMedium, setUtmMedium] = useState('')
  const [utmContent, setUtmContent] = useState('')
  const [utmTerm, setUtmTerm] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasSubmittedRef = useRef(false)

  const [formID, setFormID] = useState(initialFormID)
  useEffect(() => {
    if (initialFormID === '') {
      setFormID('contactForm-' + Math.random().toString(36).substring(2, 15))
    }
  }, [initialFormID])

  const paddingStyles = useResponsivePadding(padding)
  const style = {
    ...paddingStyles,
    backgroundColor,
    color: textColor,
    border: border ? '1px solid #e2e8f0' : 'none',
  }

  const ctaStyle = `
    ${styles['form-button']}
    ${styles[`button-fullmobile-${ctaFullMobile}`] || ''}
    ${styles[`button-position-${ctaPosition}`] || ''}
    ${styles[`button-bold-${ctaBold}`] || ''}
    ${styles[`button-alignment-${alignment}`] || ''}
  `.trim()

  const formfieldsStyle = `
    ${styles['form-fields']}
    ${styles[`form-fields-${alignment}`]}
  `.trim()

  const formdescriptionStyle = `
    ${styles['form-description']}
    ${styles[`form-description-${alignment}`]}
  `.trim()

  const formtitleStyle = `title
    ${styles['form-title']}
    ${styles[`form-title-${alignment}`]}
  `.trim()

  const formsubcontentStyle = `
    ${styles['form-subcontent']}
    ${styles[`form-subcontent-${alignment}`]}
  `.trim()

  const formdisclaimerStyle = `
    ${styles['form-disclaimer']}
    ${styles[`form-disclaimer-${disclaimer_align}`]}
  `.trim()

  const formIdClass = formID === 'details-form' ? styles['details-form'] : ''

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      setUtmSource(urlParams.get('utm_source') || '')
      setUtmMedium(urlParams.get('utm_medium') || '')
      setUtmContent(urlParams.get('utm_content') || '')
      setUtmTerm(urlParams.get('utm_term') || '')
    }
  }, [])

  useEffect(() => {
    document.querySelectorAll<HTMLElement>('.Form_row__mmeCD').forEach((row) => {
      const inputs = row.querySelectorAll('input')
      if (
        inputs.length === 1 &&
        inputs[0].type === 'hidden' &&
        inputs[0].getAttribute('data-type') !== 'multi'
      ) {
        row.style.display = 'none'
      }
    })
  }, [])

  const validateForm = (form: any) => {
    const errors: string[] = []
    const phoneRegex = /^[0-9]+$/
    if (!phoneRegex.test(form.phone) && form.phone) {
      errors.push('Phone number must be numbers only')
    }
    return errors
  }

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return
    setError('')
    setLoading(true)
    setIsSubmitting(true)

    console.warn('submitForm', loading, isSubmitting)

    const originationPath = typeof window !== 'undefined' ? window.location.pathname : '/unknown'
    const form = document.getElementById(formID) as HTMLFormElement

    const inputs = Array.from(form.getElementsByTagName('input'))
    const textareas = Array.from(form.getElementsByTagName('textarea'))
    const selects = Array.from(form.getElementsByTagName('select'))
    const labels = Array.from(form.getElementsByTagName('label'))
    const allInputs = [...inputs, ...textareas, ...selects]

    const formData: { [key: string]: string } = {}

    const enqType = form.querySelectorAll('[name="enquiryType"]') as NodeListOf<
      HTMLInputElement | HTMLSelectElement
    >
    const enquiryType = Array.from(enqType).find((node) => node.value) || enqType[0]
    const enquiryTypeValue = enquiryType?.value || ''
    formData['enquiryType'] = enquiryTypeValue

    allInputs.forEach((input) => {
      if (input.type === 'radio') {
        const radioInput = input as HTMLInputElement
        if (radioInput.checked) {
          formData[input.name] = input.value
        }
      } else {
        formData[input.name] = input.value
      }
    })

    let customEndpoint = endpoint
    if (enquiryType) {
      switch (enquiryType.value) {
        case 'Services/Business Enquiry':
          customEndpoint = 'lead/submit'
          break
        case 'Careers/Job Enquiry':
        case 'Media Enquiry':
        case 'Vendor Enquiry':
          customEndpoint = 'marketing/submit'
          break
        case 'Other':
          customEndpoint = [
            '/contact-us/general',
            '/office/melbourne',
            '/office/ballarat',
          ].includes(originationPath)
            ? 'marketing/submit'
            : 'lead/submit'
          break
        default:
          customEndpoint = 'lead/submit'
      }
    }

    const castedFormData = formData as any
    if (castedFormData.givenName && castedFormData.surname) {
      castedFormData.fullName = `${castedFormData.givenName} ${castedFormData.surname}`
    }

    for (const key in castedFormData) {
      if (key.startsWith('customField_')) {
        const customQuestionId = key.replace('customField_', '')
        const inputElement = form.querySelector(`[name="${key}"]`) as HTMLInputElement
        const labelElement = labels.find((label) => label.htmlFor === key)
        const placeholder = inputElement ? inputElement.placeholder : ''
        const question = labelElement ? labelElement.textContent || '' : placeholder
        const customAnswerId = inputElement?.getAttribute('data-customan') || ''

        castedFormData[customQuestionId] = question
        if (inputElement?.type === 'radio') {
          const legend = inputElement.closest('fieldset')?.querySelector('legend')
          castedFormData[customQuestionId] = legend?.textContent || question
        }
        castedFormData[customAnswerId] = inputElement?.value || ''
      }
    }

    Object.assign(castedFormData, {
      id: uuid,
      leadSource: lead_source || process.env.NEXT_PUBLIC_CRM_FORM_FIELD_VALUE_LEAD_SOURCE,
      Campaign_ID: campaign_id,
      leadType: lead_type || process.env.NEXT_PUBLIC_CRM_FORM_FIELD_VALUE_LEAD_TYPE,
      formSubmitted: true,
      endpoint,
      adviserInfo: 'N/A',
      industry: 'None',
      url: endpoint,
      oid: process.env.NEXT_PUBLIC_SALESFORCE_VAL_OID,
      returl: process.env.NEXT_PUBLIC_SALESFORCE_VAL_RETURL,
      Web_Form_Campaign_Id_c: campaign_id,
      errors: [],
      captcha_settings: { keyname: 'Google_Recaptcha', fallback: 'true' },
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_content: utmContent,
      utm_term: utmTerm,
    })

    if (isSubscribeForm) {
      Object.assign(castedFormData, {
        leadType: 'Individual',
        givenName: 'email',
        surname: 'subscriber',
        fullName: 'email subscriber',
        email: castedFormData.email,
      })
      customEndpoint = 'lead/submit'
    }

    const errors = validateForm(castedFormData)
    if (errors.length > 0) {
      setError('Please correct the following errors and try again: ' + errors)
      setLoading(false)
      return
    }

    const honeypotFields = ['name_first', 'email_address']
    for (const field of honeypotFields) {
      if (formData[field]?.length > 0) {
        window.location.href = '/complete'
        setLoading(false)
        return
      }
    }

    const submissionEndpoint = `${process.env.NEXT_PUBLIC_LEADS_FORM_ENDPOINT}${customEndpoint}`
    const contactService = new ContactService(submissionEndpoint)

    try {
      const salesforceForm = contactService.mapFormToSalesforce(
        castedFormData as IContactForm | any
      )
      await contactService.submitForm(salesforceForm, submissionEndpoint)
      hasSubmittedRef.current = true
      Cookies.set('form-cookie', accessCookie || 'true', { expires: 1 / 24 })
      const location = submission_url || '/thankyou'
      window.history.pushState({}, '', location)
      window.location.href = location
    } catch (err) {
      setError('An error occurred while submitting the form. Please try again.')
      console.error('Error submitting form:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className={`stylepanel ${styles[`variant-${variant}`]} ${styles['Phnxform']} ${
        styles[`shadow-${shadow === true ? 'box' : ''}`]
      } ${formIdClass}`}
      style={style}
    >
      <div
        className={`${styles['form-container']} ${isSubscribeForm ? styles['subscribe-form-container'] : ''}`}
      >
        {title && (
          <div className={formtitleStyle} data-testid="title" style={{ color: textColor }}>
            {title}
          </div>
        )}
        {description && (
          <div className={formdescriptionStyle} style={{ color: textColor }}>
            {description}
          </div>
        )}
        {subContent && (
          <div className={formsubcontentStyle} style={{ color: textColor }}>
            {render(subContent)}
          </div>
        )}
        <form onSubmit={submitForm} id={formID} name={formID}>
          <div className={formfieldsStyle}>
            <div className={styles['form-fields-col']}>
              <div className={styles['form-fields-row']} style={{ color: textColor }}>
                {fields.map((field, index) => (
                  <div className={`${styles['row']} formRowCheck`} key={index}>
                    {field}
                  </div>
                ))}
              </div>

              <label htmlFor="name" className="ohnohoney">
                Your name:
              </label>
              <input
                id="name"
                className="ohnohoney"
                autoComplete="off"
                type="text"
                name="name_first"
              />
              <label htmlFor="email" className="ohnohoney">
                Your email:
              </label>
              <input
                id="email"
                className="ohnohoney"
                autoComplete="off"
                type="email"
                name="email_address"
              />

              {error && <div className={styles.error}>{error}</div>}
              {!isSubscribeForm && (
                <div className={ctaStyle}>
                  <PhnxButton
                    label={isSubmitting ? 'Submitting...' : ctaLabel}
                    variant={ctaVariant}
                    type="submit"
                    disabled={isSubmitting || hasSubmittedRef.current}
                    css={ctaWidth ? { width: `${ctaWidth}px` } : {}}
                  />
                </div>
              )}
              {disclaimer && (
                <div className={formdisclaimerStyle} style={{ color: textColor }}>
                  {render(disclaimer)}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
